import { useCallback, useRef, useState } from 'react';
import { Check as OnlineIcon, X as OfflineIcon } from 'lucide-react';

import { classNameList } from 'utils/property';
import { DateTimeFormat, formatDateTime } from 'utils/date';
import { TranslationsKeys } from 'types/translation';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { useAuthentication } from 'hooks/useAuthentication';
import { useTranslate } from 'hooks/useTranslate';
import { useOutsideClick } from 'hooks/useOutsideClick';
import styles from './UserMenu.module.scss';

interface IProps {
  className?: string;
}

export const UserMenu = ({ className }: IProps): JSX.Element => {
  const { online, lastOnline } = useOnlineStatus();
  const { user } = useAuthentication();
  const { t } = useTranslate();
  const [open, toggleOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuId = 'user-menu';

  const handleUserMenuClick = useCallback(() => toggleOpen(!open), [open]);
  const closeNotifications = useCallback(() => toggleOpen(false), []);

  useOutsideClick(userMenuRef, closeNotifications);

  return (
    <div ref={userMenuRef} className={classNameList(styles.userMenu, className)}>
      <button
        aria-label={open ? t(TranslationsKeys.collapseUserMenuAriaLabel) : t(TranslationsKeys.expandUserMenuAriaLabel)}
        aria-haspopup="true"
        aria-controls={userMenuId}
        aria-expanded={open}
        className={classNameList(styles.userMenuIconContainer)}
        onClick={handleUserMenuClick}>
        <div className={styles.userMenuIconContainerInitials}>{user?.initials}</div>
        <div className={styles.userMenuIconContainerOnlineStatus}>
          <div
            className={classNameList(
              styles.userMenuIconContainerOnlineStatusContainer,
              online
                ? styles.userMenuIconContainerOnlineStatusContainerOnline
                : styles.userMenuIconContainerOnlineStatusContainerOffline
            )}>
            {online ? <OnlineIcon /> : <OfflineIcon />}
          </div>
        </div>
      </button>
      <div
        className={classNameList(
          styles.userMenuContainer,
          styles.userMenuContainerLoaded,
          open && styles.userMenuContainerOpen
        )}>
        {open && user && (
          <ul id={userMenuId} role="menu" aria-labelledby="extend-user-menu" aria-expanded={open}>
            <li className={styles.userMenuName}>{user.fullName}</li>
            <li className={styles.userMenuEmail}>{user.email}</li>
            <li className={styles.userMenuOnlineStatus}>
              <div
                className={classNameList(
                  styles.userMenuOnlineStatusIcon,
                  online ? styles.userMenuOnlineStatusIconOnline : styles.userMenuOnlineStatusIconOffline
                )}>
                {online ? <OnlineIcon /> : <OfflineIcon />}
              </div>
              <div>
                <div className={styles.userMenuOnlineStatusText}>
                  {online ? t(TranslationsKeys.userMenu_online) : t(TranslationsKeys.userMenu_offline)}
                </div>
                <div className={styles.userMenuOnlineStatusDescription}>
                  {online
                    ? t(TranslationsKeys.userMenu_onlineDescription)
                    : t(TranslationsKeys.userMenu_offlineDescription)}
                </div>
                {!online && (
                  <div className={styles.userMenuOnlineStatusLastOnline}>
                    {t(TranslationsKeys.userMenu_lastOnline, {
                      lastOnline: formatDateTime(lastOnline, DateTimeFormat.MEDIUM)
                    })}
                  </div>
                )}
              </div>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
};
